import server from 'api';
import type {
  AuditEntry,
  BookkeepingExport,
  Capabilities,
  Category,
  CategoryContent,
  DeliveryContent,
  DeliveryOption,
  FulfilmentStatus,
  Invoice,
  MailRecord,
  OrderDetail,
  OrderListEntry,
  Product,
  ProductContent,
  Refund,
  SettingsResponse,
  ShopCoupon,
  ShopSettings,
  StaffCatalogue,
  Stock,
} from './types';

const base = (restaurantId: string) => `/v03/shop/${encodeURIComponent(restaurantId)}`;

const get = <T,>(path: string, params?: Record<string, string>) =>
  server.get<T>(path, { params, expectedErrorStatuses: [401, 403, 404, 409, 422] }).then(({ data }) => data);
const post = <T,>(path: string, body: unknown = {}) =>
  server.post<T>(path, body, { expectedErrorStatuses: [400, 403, 404, 409, 410, 422, 503] }).then(({ data }) => data);
const put = <T,>(path: string, body: unknown) =>
  server.put<T>(path, body, { expectedErrorStatuses: [400, 403, 404, 409, 422] }).then(({ data }) => data);
const patch = <T,>(path: string, body: unknown) =>
  server.patch<T>(path, body, { expectedErrorStatuses: [400, 403, 404, 409, 422] }).then(({ data }) => data);

export type CatalogueKindPath = 'categories' | 'products' | 'delivery-options';

export const shopApi = {
  me: (rid: string) => get<Capabilities>(`${base(rid)}/me`),
  catalogue: (rid: string) => get<StaffCatalogue>(`${base(rid)}/catalogue`),

  create: <T extends CategoryContent | ProductContent | DeliveryContent>(rid: string, kind: CatalogueKindPath, draft: T) =>
    post<Category | Product | DeliveryOption>(`${base(rid)}/${kind}`, { draft }),
  saveDraft: <T extends CategoryContent | ProductContent | DeliveryContent>(rid: string, kind: CatalogueKindPath, id: string, draft: T, expectedRevision?: number) =>
    put<Category | Product | DeliveryOption>(`${base(rid)}/${kind}/${id}`, { draft, expectedRevision }),
  transition: (rid: string, kind: CatalogueKindPath, id: string, action: 'publish' | 'archive' | 'restore') =>
    post(`${base(rid)}/${kind}/${id}/${action}`),
  reorder: (rid: string, kind: 'categories' | 'delivery-options', ids: string[]) => post(`${base(rid)}/${kind}/order`, { ids }),
  reorderInCategory: (rid: string, categoryId: string, ids: string[]) => post(`${base(rid)}/categories/${categoryId}/product-order`, { ids }),
  changeStock: (
    rid: string,
    variantId: string,
    change: { type: 'set'; onHand: number; tracked?: boolean } | { type: 'adjust'; delta: number } | { type: 'tracking'; tracked: boolean },
    reason: string
  ) => post<Stock>(`${base(rid)}/stock/${variantId}`, { change, reason }),

  settings: (rid: string) => get<SettingsResponse>(`${base(rid)}/settings`),
  saveSettings: (rid: string, settings: ShopSettings) => put<Omit<SettingsResponse, 'templates' | 'placeholders'>>(`${base(rid)}/settings`, { settings }),
  publishSettings: (rid: string) => post<Omit<SettingsResponse, 'templates' | 'placeholders'>>(`${base(rid)}/settings/publish`),
  saveTemplateDraft: (rid: string, templateId: string, body: { requestId: string; baseUpdatedAt: number | null; elements: unknown; hero: string }) =>
    put<{ updatedAt: number; publishedAt: number }>(`${base(rid)}/templates/${templateId}/draft`, body),
  publishTemplate: (rid: string, templateId: string, body: { requestId: string; draftUpdatedAt: number }) =>
    post<{ updatedAt: number; publishedAt: number }>(`${base(rid)}/templates/${templateId}/publish`, body),
  coupons: (rid: string) => get<{ coupons: ShopCoupon[] }>(`${base(rid)}/coupons`).then((data) => data.coupons),
  setCouponShop: (rid: string, couponId: string, enabled: boolean) =>
    post<{ coupons: ShopCoupon[] }>(`${base(rid)}/coupons/${encodeURIComponent(couponId)}/shop`, { enabled }).then((data) => data.coupons),
  audit: (rid: string, filter: { entity?: string; entityId?: string } = {}) =>
    get<{ entries: AuditEntry[] }>(`${base(rid)}/audit`, filter as Record<string, string>).then((data) => data.entries),

  orders: (rid: string, filter: { fulfilment?: string; payment?: string; search?: string } = {}) =>
    get<{ orders: OrderListEntry[] }>(`${base(rid)}/orders`, filter as Record<string, string>).then((data) => data.orders),
  order: (rid: string, orderId: string) => get<OrderDetail>(`${base(rid)}/orders/${orderId}`),
  updateFulfilment: (rid: string, orderId: string, body: { status?: FulfilmentStatus; carrier?: string; trackingNumber?: string; trackingUrl?: string }) =>
    patch<{ eventId: string | null }>(`${base(rid)}/orders/${orderId}/fulfilment`, body),
  correctAddress: (rid: string, orderId: string, address: unknown, reason: string) =>
    patch<{ eventId: string | null }>(`${base(rid)}/orders/${orderId}/shipping-address`, { address, reason }),
  prepareMail: (rid: string, orderId: string, body: { kind: string; recipient?: string; refundId?: string; invoiceId?: string; eventId?: string }) =>
    post<MailRecord>(`${base(rid)}/orders/${orderId}/mails`, body),
  mailAction: (rid: string, orderId: string, mailId: string, action: 'approve' | 'discard' | 'retry' | 'reconcile', contentHash?: string) =>
    post<MailRecord>(`${base(rid)}/orders/${orderId}/mails/${mailId}/${action}`, { contentHash }),
  refund: (rid: string, orderId: string, body: { requestId: string; lines: { lineId: string; quantity: number }[]; includeShipping: boolean; reason: string }) =>
    post<Refund>(`${base(rid)}/orders/${orderId}/refunds`, body),
  reconcileRefunds: (rid: string, orderId: string) => post(`${base(rid)}/orders/${orderId}/refunds/reconcile`),
  restock: (rid: string, orderId: string, body: { lines: { lineId: string; quantity: number }[]; reason: string }) =>
    post(`${base(rid)}/orders/${orderId}/restock`, body),
  withdrawalProcessed: (rid: string, orderId: string, withdrawalId: string, note: string) =>
    post(`${base(rid)}/orders/${orderId}/withdrawals/${withdrawalId}/processed`, { note }),
  retrySideEffects: (rid: string, orderId: string) => post<OrderDetail>(`${base(rid)}/orders/${orderId}/side-effects`),
  invoices: (rid: string) => get<{ invoices: Invoice[] }>(`${base(rid)}/invoices`).then((data) => data.invoices),
  /** Transfers an issued document to Lexoffice now (or again); sends no email. */
  exportInvoice: (rid: string, invoiceId: string) =>
    post<{ bookkeeping: BookkeepingExport | null }>(`${base(rid)}/invoices/${encodeURIComponent(invoiceId)}/bookkeeping-export`).then((data) => data.bookkeeping),
  /**
   * Invoices are PDFs that need the signed-in user's token, so they are fetched here. The tab
   * opens within the click (popup blockers) and is filled once the PDF is there; on any failure
   * it closes again. Without a tab the PDF is downloaded rather than leaving the dashboard.
   */
  openInvoice: async (rid: string, invoiceId: string) => {
    const opened = window.open('', '_blank');
    try {
      const response = await server.get<ArrayBuffer>(`${base(rid)}/invoices/${encodeURIComponent(invoiceId)}`, {
        responseType: 'arraybuffer',
        timeout: 60000,
        expectedErrorStatuses: [403, 404, 409, 503],
      });
      const bytes = new Uint8Array(response.data);
      const type = String(response.headers?.['content-type'] ?? '');
      if (!type.startsWith('application/pdf') || String.fromCharCode(...Array.from(bytes.subarray(0, 5))) !== '%PDF-') {
        throw new Error('Der Beleg konnte nicht als PDF geladen werden');
      }
      const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
      if (opened && !opened.closed) {
        opened.location.href = url;
      } else {
        const link = document.createElement('a');
        link.href = url;
        link.download = `Beleg-${invoiceId}.pdf`;
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
      // Long enough for the viewer to read it; the tab keeps its own copy.
      window.setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (problem: any) {
      opened?.close();
      throw readableBinaryError(problem);
    }
  },
};

/** An error answer to a binary request arrives as bytes; turn its JSON back into the usual shape. */
const readableBinaryError = (problem: any) => {
  const data = problem?.response?.data;
  if (Object.prototype.toString.call(data) === '[object ArrayBuffer]' && typeof TextDecoder !== 'undefined') {
    try {
      problem.response.data = JSON.parse(new TextDecoder().decode(data));
    } catch {
      // Not JSON: the status code alone describes it.
    }
  }
  return problem;
};

export const newRequestId = () =>
  `req-${Array.from(window.crypto.getRandomValues(new Uint8Array(12)), (byte) => byte.toString(16).padStart(2, '0')).join('')}`;

export const apiProblem = (error: any): string =>
  error?.response?.data?.message || (error?.response ? `Fehler ${error.response.status}` : 'Keine Verbindung zur API');
